import React from 'react';
import { Box, styled, Typography } from '@mui/material';
import { formatPercentage, formatValue } from '../../utils/formatters';
import { useChartInteractionState } from '../ChartInteractionProvider';
import { useChartAccessorsContext } from '../ChartRoot';
import { ColorDot, highlighted, StyledLegendLabel } from './Legend.styles';
import { useLegendInteractions } from './useLegendInteractions';

export const tableLegendClassNames = {
  root: 'Chart-TableLegend',
} as const;

export const RenderTableLegend = <D,>({ data, total: totalOverride }: { data: D[]; total?: number }) => {
  const { getValue, getIdentifier, getColor, getSelected, labelFormatter } = useChartAccessorsContext();
  const { highlightedItem, isPinnable } = useChartInteractionState();
  const { onClick, onPointerEnter, onPointerLeave } = useLegendInteractions();
  const getLabel = (d: unknown) => labelFormatter?.(d, { where: 'legend' }) ?? String(getValue(d));
  const total = totalOverride ?? data.reduce((acc: number, d) => acc + getValue(d), 0);
  const uniqueId = React.useId();

  return (
    <Box
      component="ul"
      aria-label="Chart legend"
      className={tableLegendClassNames.root}
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr max-content max-content',
        overflow: 'auto',
        columnGap: 3,
        rowGap: 0,
        alignContent: 'safe center',
        padding: 0,
        margin: 0,
        width: '100%',
        height: '100%',
      }}>
      {data.map((datum, index) => {
        const id = getIdentifier(datum);
        const isHighlighted = highlightedItem === id;
        const isSelected = getSelected ? getSelected(datum) : false;
        const value = getValue(datum);
        const percentage = getValue(datum) / total;
        const color = getColor(datum);
        const text = getLabel(datum);
        const ariaLabelId = `${uniqueId}${id}_label`;
        const ariaValueId = `${uniqueId}${id}_value`;
        const ariaPercentageId = `${uniqueId}${id}_percent`;

        return (
          <StyledTableLegendLI
            key={id}
            data-highlighted={isHighlighted ? 'true' : undefined}
            data-selected={isSelected ? 'true' : undefined}
            data-interactive={isPinnable ? '' : undefined}
            data-datum-id={id}
            data-index={index}
            onPointerEnter={() => onPointerEnter(datum, id, index)}
            onPointerLeave={onPointerLeave}
            onClick={event => onClick(event, datum, id, index)}
            aria-labelledby={[ariaLabelId, ariaValueId, ariaPercentageId].join(' ')}>
            <StyledLegendLabel noWrap variant="caption" alignItems="center" display="flex" id={ariaLabelId}>
              <ColorDot color={color} />
              {React.isValidElement(text) ? (
                text
              ) : (
                <Typography variant="caption" noWrap>
                  {text}
                </Typography>
              )}
            </StyledLegendLabel>

            <StyledLegendLabel justifySelf="end" noWrap variant="caption" sx={{ color: 'var(--content-base-secondary)' }} id={ariaValueId}>
              {formatValue.format(value)}
            </StyledLegendLabel>

            <StyledLegendLabel
              justifySelf="end"
              noWrap
              variant="caption"
              sx={{ color: 'var(--content-base-secondary)' }}
              id={ariaPercentageId}>
              {formatPercentage.format(percentage)}
            </StyledLegendLabel>
          </StyledTableLegendLI>
        );
      })}
    </Box>
  );
};

const StyledTableLegendLI = styled('li')(() => ({
  gridColumn: '1 / -1',
  display: 'grid',
  gridTemplateColumns: 'subgrid',
  alignContent: 'center',
  padding: '5px 12px',
  alignItems: 'center',
  ...highlighted(),
}));
